import type React from "react";
import { memo, useEffect, useMemo, useState } from "react";

import { INVOICE_OVERVIEW_CONFIG_QUERY } from "@/apis/gql/apolloClient";
import UploadProgressBox from "@/features/upload-progress-box/UploadProgressBox";
import type { RawFilters, ServerFilters } from "@/hooks/useQueryFilters";
import type { Exports } from "@/lib/constants/exports";
import type { InvoiceTypeString } from "@/lib/constants/invoice-type";
import { useInvoicesContext } from "@/providers/invoices";
import type { InvoiceFilterFields } from "@/types/utils";
import { type ApolloError, useQuery } from "@apollo/client";
import type { OnChangeFn, SortingState } from "@tanstack/react-table";
import { InvoiceTableActionBar } from "./invoice-table-action-bar";
import { InvoicesDataTable } from "./invoices-data-table";
import { InvoicesOverviewFilters } from "./invoices-filters";
import { InvoiceTableInstanceProvider } from "./table-instance-provider";

type Props = {
  tableName: string;
  enableActions?: boolean;
  enableFilters?: boolean;
  showConfidences?: boolean;
  showStartReviewButton?: boolean;
  showUploadProgress?: boolean;
  columnVisibility?: Record<string, boolean>;
  actionsConfig?: {
    exportButton?: {
      isVisible: boolean;
      options?: Exports[];
    };
    showDeleteButton?: boolean;
    showStatusUpdateButton?: boolean;
    showTypeUpdateButton?: boolean;
    showRetryAiButton?: boolean;
  };
  filtersConfig?: {
    showTypeFilter?: boolean;
    showUploadButton?: boolean;
    showStatusFilter?: boolean;
    showAccountFilter?: boolean;
    showSupplierFilter?: boolean;
    showExportsFilter?: boolean;
    showOcrReviewedFilter?: boolean;
    showExportAllButton?: boolean;
    autoFocus?: boolean;
  };
  initialSortingOrder?: {
    id: string;
    desc: boolean;
  }[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  filters: RawFilters<InvoiceFilterFields>;
  setFilters: any;
  globalSearch: string;
  setGlobalSearch: (value: string) => void;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  error: ApolloError | undefined;
  readOnly?: boolean;
  invoiceType?: InvoiceTypeString;
  initialServerSideFilters: ServerFilters<InvoiceFilterFields>;
  globalServerSideSearch: string;
  serverSideFilters: ServerFilters<InvoiceFilterFields>;
  resetFilters: () => void;
  emptyStateComponent?: React.ReactNode;
};

function _InvoiceOverviewTable<TData, TValue>({
  tableName,
  columnVisibility,
  enableActions = true,
  enableFilters = true,
  filtersConfig = {},
  actionsConfig = {},
  showUploadProgress = true,
  showConfidences = false,
  showStartReviewButton = false,
  readOnly = false,
  totalItems,
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  onPageSizeChange,
  filters,
  setFilters,
  globalSearch,
  setGlobalSearch,
  sorting,
  onSortingChange,
  error,
  invoiceType,
  initialServerSideFilters,
  globalServerSideSearch,
  serverSideFilters,
  resetFilters,
  emptyStateComponent,
}: Props) {
  const { invoices } = useInvoicesContext();

  const { data: config } = useQuery(INVOICE_OVERVIEW_CONFIG_QUERY);

  const [filteredInvoices, setFilteredInvoices] = useState(invoices);

  useEffect(() => {
    setFilteredInvoices(invoices);
  }, [invoices]);

  const columnFilters = useMemo(() => {
    return Object.entries(filters).map(([key, value]) => ({
      id: key,
      value,
    }));
  }, [filters]);

  return (
    <InvoiceTableInstanceProvider
      tableName={tableName}
      data={filteredInvoices}
      showConfidences={showConfidences}
      columnVisibility={columnVisibility}
      totalItems={totalItems}
      sorting={sorting}
      onSortingChange={onSortingChange}
      columnFilters={columnFilters}
      readOnly={readOnly}
      invoiceType={invoiceType}
      initialServerSideFilters={initialServerSideFilters}
      globalServerSideSearch={globalServerSideSearch}
      serverSideFilters={serverSideFilters}
      onServerSideFiltersClear={resetFilters}
    >
      <div className="flex flex-col h-full gap-4">
        {enableFilters && (
          <InvoicesOverviewFilters
            tableName={tableName}
            data={config}
            onFilter={setFilteredInvoices}
            filtersConfig={filtersConfig}
            showStartReviewButton={showStartReviewButton}
            filters={filters}
            setFilters={setFilters}
            globalSearch={globalSearch}
            setGlobalSearch={setGlobalSearch}
            invoiceType={invoiceType}
          />
        )}
        <div className="flex-1">
          <InvoicesDataTable
            totalItems={totalItems}
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            error={error}
            emptyStateComponent={emptyStateComponent}
          >
            {enableActions && (
              <InvoiceTableActionBar
                invoiceType={invoiceType}
                actionsConfig={actionsConfig}
              />
            )}
            {showUploadProgress && <UploadProgressBox />}
          </InvoicesDataTable>
        </div>
      </div>
    </InvoiceTableInstanceProvider>
  );
}

export const InvoiceOverviewTable = memo(_InvoiceOverviewTable);
